// Chart view of a patient's Orders & Followups, backed by `clinical_action_items`.
//
// Two things happen here. It SURFACES rows that already exist — written by the
// copilot when a recording's snapshot is saved (`transcription_backup.py` →
// `clinic_orders.persist_action_items_for_snapshot`) or typed by staff in the
// Compliance dashboard. And on open it SCANS the visit note's ORDERS block via
// `/api/clinic/patient-orders/scan-note/`, because orders typed into the note
// never reached that table before (see api/patient_orders_scan.py). Items can
// be assigned here (multi-select) and moved through the review flow
// (backend/api/task_review.py).
//
// ── Why it fetches the whole clinic list ────────────────────────────────────
// The obvious call is `fetchOrdersForAudio(ehrId, null)`, which filters
// server-side on `ehr_id`. That silently loses rows, because a blank `ehr_id`
// is NORMAL on audio-derived items: `normalizeEhrForCopilotFilename`
// (pages/AudioRecorder/utils/copilotBackupUtils.js) maps the `unknown_ehr`
// placeholder to "", and the audio-filename fallback yields "" whenever the
// stem has no name-EHR boundary. The column is `NOT NULL DEFAULT ''`, so those
// rows are real but unreachable by an `ehr_id` filter.
//
// `fetchOrders` returns rows already grouped by patient, and the backend's
// `_patient_group_key` prefers the patient NAME (falling back to ehr_id only
// when one name maps to several EHRs) — exactly the matching we need. So we
// take the grouped payload and pick our patient out of it locally.
//
// ── Why it reads two schemas ────────────────────────────────────────────────
// Rows do not reliably live in the chart's own schema. The WRITE path takes
// its schema from the recorder's guess: `persist_action_items_for_snapshot`
// is called with `schema_name = data.get('schema_name', 'public')`, and the
// frontend derives that from the audio's storage path, falling back to the
// literal "public" when the path carries no schema segment
// (copilotBackupUtils.js `folderFromObjectPath`). Every read endpoint in
// clinic_orders.py defaults to 'public' too. The practical result is that a
// tenant patient's orders are usually sitting in `public.clinical_action_items`
// even though their chart is in, say, DocPon.
//
// Querying only `chartDocSchema` therefore shows an empty list for patients
// who demonstrably have orders. We query the chart schema AND public, then
// merge and de-duplicate by row id. `clinic_orders_list` already swallows a
// missing-table error and returns an empty payload, so asking for a schema
// that has no table is harmless.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchOrders, createOrderItem, assignOrderItem } from "../../services/clinicComplianceService";
import {
  listOrderTemplates,
  authedFetch,
} from "../../services/orderPaperwork";
import OrderPaperworkEditor from "./OrderPaperworkEditor";
import PaperworkTab from "./PaperworkTab";
import SendSignedOrderModal from "./SendSignedOrderModal";
import { hashNoteDelta, noteScanMark, rememberNoteScan } from "../../utils/noteScanCache";
import { normalizeStatus, STATUS_META } from "../../utils/workItemStatus";
import { matchPatientGroups, uniqueOrderItems } from "../../utils/scheduleRowActions";
import { onWorkItemsChanged, emitWorkItemsChanged } from "../../utils/workItemsBus";
import WorkItemActions from "../../components/messaging/WorkItemActions";
import ColleaguePicker from "../../components/messaging/ColleaguePicker";
import PersonChip from "../../components/messaging/PersonChip";
import WorkItemActivity from "../../components/messaging/WorkItemActivity";
import useWorkItemSummaries from "../../components/messaging/useWorkItemSummaries";
import { summaryKey } from "../../utils/workItemRefs";
import { useUserInfo } from "../../hooks/medstudent/useUserInfo";
// Read the visit note's ORDERS block and file anything new into
// `clinical_action_items`. Idempotent: the backend dedups against what is
// already on the patient's list (fuzzily, so a note order collapses against
// the same order dictated into the recorder), so a re-scan reports
// `skipped` rather than piling up duplicates.
// The live editor contents, read at click time so a scan that races the
// autosave debounce still sees orders typed seconds ago.
function liveNoteDelta(currentVisitOverride) {
  if (!currentVisitOverride || !currentVisitOverride.path) return null;
  try {
    const quill = currentVisitOverride.getQuill && currentVisitOverride.getQuill();
    return quill && quill.getContents ? quill.getContents() : null;
  } catch (err) {
    console.warn("ClinicPatientOrdersModal: live delta unavailable", err);
    return null;
  }
}

async function scanNoteForOrders({ patientId, currentVisitOverride, delta = undefined }) {
  const body = { patient_id: patientId };
  if (currentVisitOverride && currentVisitOverride.path) {
    if (delta === undefined) delta = liveNoteDelta(currentVisitOverride);
    if (delta) {
      body.current_visit_override = {
        visit_id: currentVisitOverride.visitId || "",
        path: currentVisitOverride.path,
        schema: currentVisitOverride.schema || "",
        delta,
      };
    }
  }

  // Shares authedFetch with every other orders endpoint: no anonymous request
  // is ever sent, and an expired token is refreshed rather than surfacing as
  // the bare word "unauthenticated".
  return authedFetch("clinic/patient-orders/scan-note/", { body });
}

// Patient matching (normalizeName / nameKey / matchPatientGroups) lives in
// utils/scheduleRowActions.js, shared with the Schedules row actions so the
// counts on a schedule row and this list always agree. The chart stores
// "Carson, James"; the orders table stores whatever the recorder had
// ("James Carson", "Carson_James"), so names match order-insensitively.

// `session_id` carries provenance. patient_orders_scan writes `note:<visit_id>`;
// a hand-added item writes `manual:<tab>:<who>` so it can be shown alongside
// the items it belongs with, credited to whoever typed it. Encoding it here
// rather than adding a column keeps a live table untouched.
function manualProvenance(item) {
  if (item?.source !== "manual") return null;
  const raw = String(item.session_id || "");
  if (!raw.startsWith("manual:")) {
    // Added before the author was recorded — say so honestly rather than
    // inventing a name or hiding the row.
    return { tab: "note", who: "" };
  }
  const rest = raw.slice("manual:".length);
  const cut = rest.indexOf(":");
  const tab = cut === -1 ? rest : rest.slice(0, cut);
  const who = cut === -1 ? "" : rest.slice(cut + 1);
  return { tab: tab === "audio" ? "audio" : "note", who };
}

// `scheduled_date` is a plain YYYY-MM-DD from Postgres. Parsing it with
// `new Date()` would shift it a day in negative-offset timezones, so format
// the parts directly.
function prettyDate(iso) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(iso || "").trim());
  if (!m) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m[2], 10) - 1] || m[2]} ${parseInt(m[3], 10)}, ${m[1]}`;
}

// Select-all for the tab in view. `indeterminate` is a DOM property with no
// JSX equivalent, so it has to be written through a ref — without it a partial
// selection would render as an empty box, claiming nothing is picked.
function SelectAllBox({ items, selectedIds, onChange }) {
  const ref = useRef(null);
  const picked = items.filter((i) => selectedIds.has(i.id)).length;
  const all = items.length > 0 && picked === items.length;
  const some = picked > 0 && !all;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);

  if (!items.length) return null;

  return (
    <label className="mb-2 flex items-center gap-2 text-[11px] font-semibold text-slate-500 dark:text-slate-400 cursor-pointer w-fit">
      <input
        ref={ref}
        type="checkbox"
        checked={all}
        onChange={() => onChange(!all)}
        aria-label={`Select all ${items.length} item${items.length === 1 ? "" : "s"} on this tab`}
      />
      Select all ({items.length})
    </label>
  );
}

function ItemRow({ item, index, selected, onToggle, schema, currentUserId, summary, onChanged }) {
  const [showActivity, setShowActivity] = useState(false);
  const [commentFocus, setCommentFocus] = useState(0);
  // Comments the open activity has shown; counts only grow (see TasksPanel).
  const [seenComments, setSeenComments] = useState(0);
  const comments = Math.max(summary?.comments || 0, seenComments);
  const normalized = normalizeStatus(item.status);
  const meta = STATUS_META[normalized] || STATUS_META.unassigned;
  // The row's own schema: a tenant chart's orders often live in public, and
  // a write to the chart schema would miss them.
  const itemSchema = item._schema || schema;
  // People on it, or a history (an order unassigned with a reason still has one).
  const hasActivity = Boolean(item.assigned_to_user_id || item.assigned_by_user_id || summary);
  return (
    <li className="flex items-start gap-3 py-2.5 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
      {/* Ticking items is how a form gets generated: the order request carries
          the items chosen here, not everything on the patient's record. */}
      <input
        type="checkbox"
        className="mt-1 shrink-0"
        checked={selected}
        onChange={() => onToggle(item.id)}
        aria-label={`Select: ${item.description}`}
      />
      <span className="mt-0.5 shrink-0 w-6 text-right text-xs font-semibold tabular-nums text-slate-400 dark:text-slate-500">
        {index}.
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-800 dark:text-slate-100 break-words">
          {item.description}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ring-1 ${meta.tw}`}>
            {meta.label}
          </span>
          {item.scheduled_date && <span>· {prettyDate(item.scheduled_date)}</span>}
          {/* `source` says where the item came from, so a clinician can tell
              what the AI heard in a recording from what it read in the note
              from what a person entered deliberately. */}
          <span>
            ·{" "}
            {item.source === "manual"
              ? `✍️ added by ${manualProvenance(item)?.who || "staff"}`
              : item.source === "note"
                ? "📝 from note"
                : "🎙 from recording"}
          </span>
          {item.audio_number != null && <span>· Audio {item.audio_number}</span>}
        </div>
        {(item.assigned_to?.name || item.assigned_to?.email) && (
          <div className="mt-1.5">
            <PersonChip label="Assigned to" person={{ name: item.assigned_to.name, email: item.assigned_to.email,
                                                      current_schema: item.assigned_to.schema }} />
          </div>
        )}
        {normalized === "rejected" && summary?.rejection?.body && (
          <div className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">
            Rejected: “{summary.rejection.body}”
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <WorkItemActions
            item={{
              kind: "order",
              id: item.id,
              schema: itemSchema,
              status: normalized,
              assignedTo: item.assigned_to_user_id,
              assignedBy: item.assigned_by_user_id,
              title: item.description,
            }}
            currentUserId={currentUserId}
            currentUserSchema={schema}
            showStatus={false}
            onChanged={onChanged}
            onComment={() => {
              setShowActivity(true);
              setCommentFocus((n) => n + 1);
            }}
          />
          {hasActivity && (
            <button type="button" aria-expanded={showActivity} onClick={() => setShowActivity((v) => !v)}
                    className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">
              {comments ? `Activity · 💬 ${comments}` : "Activity"}
            </button>
          )}
        </div>
        {showActivity && (
          <WorkItemActivity
            item={{ kind: "order", id: item.id, schema: itemSchema, assignedTo: item.assigned_to_user_id,
                    assignedBy: item.assigned_by_user_id }}
            currentUserId={currentUserId}
            focusComment={commentFocus}
            onCommentCount={setSeenComments}
          />
        )}
      </div>
    </li>
  );
}

function Section({ title, icon, items, emptyText, selectedIds, onToggle, schema, currentUserId, summaries, onChanged }) {
  return (
    <div className="mb-5 last:mb-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
        {icon} {title} {items.length > 0 && `(${items.length})`}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400 py-1">{emptyText}</div>
      ) : (
        <ol className="list-none m-0 p-0">
          {items.map((item, i) => (
            <ItemRow
              key={item.id || `${title}-${i}`}
              item={item}
              index={i + 1}
              selected={selectedIds.has(item.id)}
              onToggle={onToggle}
              schema={schema}
              currentUserId={currentUserId}
              summary={summaries?.[summaryKey({ kind: "order", id: item.id, schema: item._schema || schema })]}
              onChanged={onChanged}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

// Add an order or followup by hand.
//
// Not everything is dictated into a recording or typed into the note's ORDERS
// block — staff need to add an item directly, and it has to behave like any
// other item afterwards: selectable, and usable on a generated form. It is
// dated to the VISIT so it lands under "This visit" rather than nowhere.
function AddItemForm({ patient, schema, visitDate, tab, addedBy, onAdded, onCancel }) {
  const [itemType, setItemType] = useState("order");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    const text = description.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      await createOrderItem(
        {
          item_type: itemType,
          description: text,
          patient_name: patient?.patient_name || "",
          ehr_id: String(patient?.EHR_ID || patient?.ehr_id || "").trim(),
          scheduled_date: String(visitDate || "").slice(0, 10) || null,
          // Which tab it was added from, and by whom. See manualProvenance.
          session_id: `manual:${tab === "audio" ? "audio" : "note"}:${addedBy || ""}`,
        },
        { schema },
      );
      setDescription("");
      onAdded();
    } catch (err) {
      // Keep what was typed — a transport blip should not cost the wording.
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
      {error && (
        <div
          role="alert"
          className="mb-2 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-2 py-1.5 text-xs font-semibold text-rose-800 dark:text-rose-200"
        >
          {error}
        </div>
      )}
      <div className="flex items-end gap-2">
        <div>
          <label htmlFor="add-item-type" className="block text-[11px] font-semibold mb-1">
            Type
          </label>
          <select
            id="add-item-type"
            value={itemType}
            onChange={(e) => setItemType(e.target.value)}
            className="rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1 text-xs"
          >
            <option value="order">Order</option>
            <option value="followup">Followup</option>
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor="add-item-text" className="block text-[11px] font-semibold mb-1">
            What needs doing
          </label>
          <input
            id="add-item-text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            placeholder="Order vitamin D level"
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1 text-sm"
          />
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy || !description.trim()}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm disabled:opacity-50"
        >
          {busy ? "Adding…" : "Add"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs px-2 py-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


export default function ClinicPatientOrdersModal({
  patient,
  schema,
  onClose,
  // When true, opening the modal also reads the note's ORDERS block before
  // listing — so the clinician doesn't have to press a second button to see
  // orders they just typed. The scan is idempotent, so this is safe to repeat.
  autoScan = false,
  currentVisitOverride = null,
  // The visit currently on screen (`visit.date_`). When present, the list is
  // scoped to this date only -- without it, every order/followup ever
  // recorded for the patient showed up together, which buried the current
  // visit's items under the patient's whole history.
  visitDate = "",
  // Printed under the mark on anything this clinician signs.
  signerName = "",
  // Credited on anything this clinician adds by hand.
  currentUserName = "",
  // Who is acting, for the review flow (assign / complete / confirm). Task 6
  // passes this from MedStudentClinicPage; when absent (older callers, or a
  // render that predates that wiring) fall back to the signed-in user.
  currentUserId,
  // "all" opens on every date instead of this visit. The Schedules row
  // actions pass it: their Orders count covers the patient's whole list, so
  // opening onto one visit would hide the older items that count included.
  // Default (null) keeps the visit-first behaviour below.
  initialScope = null,
  // A task's "Open in chart" lands here: the Paperwork tab, with that form in
  // the editor.
  initialTab = null,
  openPaperworkId = null,
}) {
  const me = useUserInfo();
  const actorId = currentUserId || me.userId;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [items, setItems] = useState([]);
  // Names present in the orders table when none of them matched this chart.
  const [candidates, setCandidates] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [scanInFlight, setScanInFlight] = useState(false);
  const [flash, setFlash] = useState(null);
  // The auto-scan must run before the first list load, but only once per
  // mount — otherwise Refresh would re-run an expensive LLM call.
  const [autoScanDone, setAutoScanDone] = useState(!autoScan);

  // Any other open surface (audio panel, Compliance tab) mutating an order
  // should refresh this list too.
  // A comment changes no order row: reloading would only unmount the rows and
  // close the activity panel the comment was just posted from.
  useEffect(
    () => onWorkItemsChanged((e) => {
      if (e?.detail?.action !== "comment") setRefreshKey((k) => k + 1);
    }),
    []
  );

  const patientName = patient?.patient_name || "";
  const ehrId = String(patient?.EHR_ID || "").trim();
  const patientId = patient?.id || "";

  const runScan = useCallback(async () => {
    setScanInFlight(true);
    setFlash(null);
    const delta = liveNoteDelta(currentVisitOverride);
    const noteHash = hashNoteDelta(delta);
    try {
      const payload = await scanNoteForOrders({
        patientId,
        currentVisitOverride,
        delta,
      });
      // Remembered only on a real answer, so a failed read is retried next
      // time rather than cached as "done".
      if (payload?.status === "ok" || payload?.status === "no_orders") {
        rememberNoteScan(patientId, noteHash);
      }
      const count = payload?.count || 0;
      const skipped = payload?.skipped || 0;
      if (payload?.status === "ok" && count > 0) {
        setFlash({
          type: "success",
          text: `Added ${count} order${count === 1 ? "" : "s"} from the note${
            skipped ? ` · ${skipped} already on the list` : ""
          }.`,
        });
      } else if (payload?.status === "ok" || payload?.status === "no_orders") {
        setFlash({
          type: "info",
          text: skipped
            ? `The note's orders are already on the list (${skipped}).`
            : "No orders block found in this visit note.",
        });
      } else {
        setFlash({ type: "error", text: payload?.error || "Could not read the note." });
      }
    } catch (err) {
      setFlash({ type: "error", text: err?.message || String(err) });
    } finally {
      setScanInFlight(false);
      setRefreshKey((k) => k + 1);
    }
  }, [patientId, currentVisitOverride]);

  // Auto-scan on open, before the first load — unless this exact note was
  // already read. The modal remounts on every open, so without this the
  // clinician paid for a fresh read of an unchanged note each time.
  useEffect(() => {
    if (autoScanDone || !patientId) return;
    setAutoScanDone(true);
    const noteHash = hashNoteDelta(liveNoteDelta(currentVisitOverride));
    if (noteScanMark(patientId) === noteHash) {
      console.log("[Orders] note unchanged since its last read — skipping the scan");
      return;
    }
    runScan();
  }, [autoScanDone, patientId, runScan, currentVisitOverride]);

  useEffect(() => {
    // Hold the list until the opening scan has filed whatever it found, so
    // the clinician never sees a stale "no orders" that the scan is about to
    // contradict a few seconds later.
    if (!autoScanDone || scanInFlight) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // The chart's schema first, then public — see the note at the top of
        // this file on why the rows are usually not where the chart is.
        const schemas = schema && schema !== "public" ? [schema, "public"] : ["public"];
        const results = await Promise.all(schemas.map((s) => fetchOrders({ schema: s })));
        if (cancelled) return;
        // Only a total outage is an error. One schema failing while the other
        // answers is normal — a tenant may legitimately have no table.
        if (results.every((r) => r?._error)) {
          setLoadError("Could not reach the orders service just now. Try again in a minute.");
          setItems([]);
          return;
        }
        // Pick our patient out of the grouped payloads. Match on the
        // normalized name first (the backend's own preferred key) and fall
        // back to ehr_id, so a row whose `patient_name` is spelled slightly
        // differently but carries the right EHR still shows up.
        // Tag every row with the schema it came from — every write (assign,
        // status change) must go back to that schema, not the chart's.
        const allGroups = results.flatMap((data, i) =>
          (data?.patients || []).map((g) => ({
            ...g,
            items: (g.items || []).map((it) => ({ ...it, _schema: schemas[i] })),
          }))
        );
        const matched = matchPatientGroups(allGroups, { patientName, ehrId });
        // When nothing matches, keep the names we DID see. `ehr_id` is blank
        // on most audio-derived rows, so the name is the only join key — and
        // when it disagrees with the chart the failure is otherwise invisible
        // ("no orders for this patient" looks identical to "none exist").
        // Showing the candidates turns that dead end into a diagnosis.
        setCandidates(
          matched.length === 0
            ? allGroups
                .map((g) => ({
                  name: g.patient_name || "(no name)",
                  ehrId: g.ehr_id || "",
                  count: (g.items || []).length,
                }))
                .filter((c) => c.count > 0)
            : []
        );
        // The same row can surface from both schemas only if a tenant and
        // public genuinely hold copies; key on id so it is listed once.
        setItems(uniqueOrderItems(matched.flatMap((g) => g.items || [])));
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || String(err));
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientName, ehrId, schema, refreshKey, autoScanDone, scanInFlight]);

  // scheduled_date is a plain `YYYY-MM-DD` (see `_normalize_scheduled_date`
  // server-side); normalize visitDate the same way before comparing so a
  // timestamp-with-time value still matches.
  const normalizedVisitDate = useMemo(() => {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(visitDate || "").trim());
    return m ? m[1] : "";
  }, [visitDate]);

  // Rows whose scheduled_date matches the chart's visit.
  const visitScoped = useMemo(() => {
    if (!normalizedVisitDate) return items;
    return items.filter(
      (i) => String(i.scheduled_date || "").slice(0, 10) === normalizedVisitDate
    );
  }, [items, normalizedVisitDate]);

  // "visit" | "all". Narrowing to the visit is right when the visit HAS rows
  // and actively misleading when it does not: an empty list then looks
  // identical to "this patient has no orders", which is how the Orders button
  // came to look broken. So the scope widens itself, visibly, and the toggle
  // lets the clinician narrow it back.
  const [scopeOverride, setScopeOverride] = useState(
    initialScope === "all" || initialScope === "visit" ? initialScope : null
  );
  const dateScope =
    scopeOverride || (visitScoped.length === 0 && items.length > 0 ? "all" : "visit");
  const visitItems = dateScope === "all" ? items : visitScoped;

  // Two tabs instead of one long combined list -- audio-derived items
  // routinely outnumber note-derived ones many times over.
  // Two tabs. A hand-added item joins the tab it was added from rather than
  // getting one of its own — it is part of that visit's working list, and its
  // badge already says who added it.
  const inTab = useCallback((item, tab) => {
    const manual = manualProvenance(item);
    if (manual) return manual.tab === tab;
    return tab === "note" ? item.source === "note" : item.source !== "note";
  }, []);
  const noteItems = useMemo(
    () => visitItems.filter((i) => inTab(i, "note")), [visitItems, inTab]);
  const audioItems = useMemo(
    () => visitItems.filter((i) => inTab(i, "audio")), [visitItems, inTab]);

  // Never open onto an empty tab. There is not one source='note' row in any
  // schema in production, so a hard "note" default hid every audio order
  // behind a tab nobody thought to click.
  // Paperwork — the PDF/photo side of Orders (PaperworkTab). A Send button
  // there opens the fax/email window here: { doc, mode }.
  const [sendTarget, setSendTarget] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  // Non-null once Generate order is pressed: the ids that will fill the form.
  const [generateFor, setGenerateFor] = useState(null);
  // A form row's Fill out (or a task's link): the editor on the form's clean
  // source, saving back to that form. A template's Fill out is today's flow.
  const openFormInEditor = useCallback((form) => {
    setGenerateFor(null);
    setOpenDoc(form.template
      ? { path: form.path, filename: form.name }
      : { path: form.original_path || form.path, filename: form.name, paperworkId: form.id });
  }, []);
  // A task link's form opens once. The tab unmounts while the editor is up,
  // so the "already opened" mark lives here: Back shows the list.
  const [linkedPaperworkId, setLinkedPaperworkId] = useState(openPaperworkId);
  const linkedPaperworkOpened = useCallback(() => setLinkedPaperworkId(null), []);

  // The blank forms this clinic can fill, the AI's pick for what is currently
  // ticked, and any override the clinician has made.
  const [templatesError, setTemplatesError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateGroup, setTemplateGroup] = useState("");
  const [suggestedTemplate, setSuggestedTemplate] = useState(null);
  const [chosenTemplate, setChosenTemplate] = useState(null);

  // Ticked items, held across BOTH tabs — an order request routinely draws
  // some items the recorder heard and some the clinician typed.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const [addingItem, setAddingItem] = useState(false);

  // Assign-to picker for the ticked items. Separate from the order-form
  // picker below — ticking is shared between the two actions, but only one
  // of these panels is open at a time.
  const [assigning, setAssigning] = useState(false);
  const [assignee, setAssignee] = useState(null);
  const [assignBusy, setAssignBusy] = useState(false);

  const handleAssignSelected = async () => {
    if (!assignee) return;
    setAssignBusy(true);
    const ids = Array.from(selectedIds);
    const schemaById = new Map(items.map((it) => [it.id, it._schema]));
    const results = await Promise.allSettled(
      ids.map((id) =>
        assignOrderItem(id, assignee.id, { assignedBy: actorId, schema: schemaById.get(id) || schema })
      )
    );
    setAssignBusy(false);
    const failedIds = ids.filter((_, i) => results[i].status === "rejected");
    const who = assignee.name || assignee.email;
    if (failedIds.length === 0) {
      setFlash({ type: "success", text: `Assigned ${ids.length} to ${who}` });
      setSelectedIds(new Set());
      setAssigning(false);
      setAssignee(null);
    } else {
      setSelectedIds(new Set(failedIds));
      setFlash({
        type: "error",
        text: `${ids.length - failedIds.length} of ${ids.length} assigned — the rest are still selected`,
      });
    }
    setRefreshKey((k) => k + 1);
    if (failedIds.length < ids.length) emitWorkItemsChanged({ kind: "order" });
  };

  // Two loads on purpose. The first, with no ids, just fills the dropdown —
  // it costs nothing. The second asks which form fits what is ticked, and is
  // debounced so ticking five boxes is one question, not five.
  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    const ids = Array.from(selectedIds);
    const ask = () => {
      listOrderTemplates({ patientId, orderIds: ids })
        .then((r) => {
          if (cancelled) return;
          setTemplates(r?.templates || []);
          setTemplateGroup(r?.group || "");
          if (r?.suggested) setSuggestedTemplate(r.suggested);
        })
        .catch((err) => {
          if (!cancelled) setTemplatesError(err?.message || String(err));
        });
    };
    if (!ids.length) {
      ask();
      return () => { cancelled = true; };
    }
    const t = setTimeout(ask, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [patientId, selectedIds]);

  // "All" means all of what is in front of you. Selection is shared across
  // tabs deliberately, so this must not disturb the tab you cannot see.
  const setAllOnTab = useCallback((items, on) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      items.forEach((i) => (on ? next.add(i.id) : next.delete(i.id)));
      return next;
    });
  }, []);

  const [tabOverride, setTabOverride] = useState(initialTab);
  const activeTab =
    tabOverride || (noteItems.length ? "note" : audioItems.length ? "audio" : "note");
  const tabItems = activeTab === "note" ? noteItems : audioItems;

  // Newest first within each group — `created_at` is the only field every row
  // is guaranteed to have (scheduled_date is backfilled best-effort).
  const { orders, followups } = useMemo(() => {
    const sorted = [...tabItems].sort((a, b) =>
      String(b.created_at || "").localeCompare(String(a.created_at || ""))
    );
    return {
      orders: sorted.filter((i) => i.item_type !== "followup"),
      followups: sorted.filter((i) => i.item_type === "followup"),
    };
  }, [tabItems]);

  // Each assigned row's 💬 count and latest rejection message.
  const summaryRefs = useMemo(
    () => tabItems.map((i) => ({ kind: "order", id: i.id, schema: i._schema || schema })),
    [tabItems, schema]
  );
  const summaries = useWorkItemSummaries(summaryRefs);

  const openCount = useMemo(
    () => visitItems.filter((i) => normalizeStatus(i.status) !== "completed").length,
    [visitItems]
  );

  // The form that will be filled: the clinician's choice if they made one,
  // otherwise the AI's suggestion, otherwise the first available.
  const activeTemplate = useMemo(() => {
    if (!templates.length) return null;
    const byName = (n) => templates.find((t) => t.name === n);
    return byName(chosenTemplate) || byName(suggestedTemplate) || templates[0];
  }, [templates, chosenTemplate, suggestedTemplate]);

  // Refresh re-reads the note, not just the list. With the opening read
  // cached, this is the one way to force one — say, after deleting an order
  // that should be re-filed from the note. runScan reloads the list when it
  // finishes, so the two happen in order.
  const handleRefresh = useCallback(() => { runScan(); }, [runScan]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clinic-orders-modal-title"
      onClick={onClose}
    >
      {/* Wide by default: the Paperwork tab renders a full page of paperwork
          that has to be legible enough to check before it is signed. */}
      <div
        className="bg-white dark:bg-[#0a0a0f] text-slate-900 dark:text-slate-100 rounded-xl shadow-xl w-[1320px] max-w-[97vw] h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 id="clinic-orders-modal-title" className="text-base font-semibold flex items-center gap-2">
              📋 Orders &amp; followups
            </h2>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {patientName ? `For ${patientName} · ` : ""}
              {loading ? "loading…" : `${openCount} open of ${visitItems.length}`}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200 disabled:opacity-60"
              onClick={handleRefresh}
              disabled={loading || scanInFlight}
            >
              ↻ Refresh
            </button>
            <button
              type="button"
              aria-label="Close"
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>

        {flash && (
          <div
            role="status"
            className={
              flash.type === "error"
                ? "mx-5 mt-3 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-3 py-2 text-sm font-semibold text-rose-800 dark:text-rose-200"
                : flash.type === "success"
                  ? "mx-5 mt-3 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/[0.08] px-3 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-200"
                  : "mx-5 mt-3 rounded-md border border-sky-300 bg-sky-50 dark:bg-sky-500/[0.08] px-3 py-2 text-sm font-semibold text-sky-800 dark:text-sky-200"
            }
          >
            {flash.text}
            <button type="button" onClick={() => setFlash(null)} className="ml-2 text-xs underline">
              dismiss
            </button>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-auto px-5 py-4">
          {scanInFlight && activeTab !== "paperwork" && (
            <div
              className="flex flex-col items-center justify-center gap-3 py-12 text-slate-500 dark:text-slate-400"
              role="status"
              aria-live="polite"
            >
              <div className="h-10 w-10 rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-amber-500 animate-spin" />
              <div className="text-sm font-semibold">Reading the visit note…</div>
              <div className="text-xs">Extracting the orders block, then filing anything new.</div>
            </div>
          )}

          {!scanInFlight && loading && activeTab !== "paperwork" && (
            <div
              className="flex flex-col items-center justify-center gap-3 py-12 text-slate-500 dark:text-slate-400"
              role="status"
              aria-live="polite"
            >
              <div className="h-10 w-10 rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-sky-500 animate-spin" />
              <div className="text-sm font-semibold">Loading orders…</div>
            </div>
          )}

          {!scanInFlight && !loading && loadError && (
            <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-3 py-2 text-sm font-semibold text-rose-800 dark:text-rose-200">
              {loadError}
            </div>
          )}

          {!scanInFlight && !loading && !loadError && activeTab !== "paperwork" && (
            addingItem ? (
              <AddItemForm
                patient={patient}
                schema={schema}
                visitDate={visitDate}
                tab={activeTab}
                addedBy={currentUserName}
                onAdded={() => {
                  setAddingItem(false);
                  setRefreshKey((k) => k + 1);
                }}
                onCancel={() => setAddingItem(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setAddingItem(true)}
                className="mb-4 text-xs font-semibold px-2.5 py-1 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/40"
              >
                + Add item
              </button>
            )
          )}

          {!scanInFlight && !loading && !loadError && activeTab !== "paperwork" && visitItems.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
              {items.length > 0
                ? `No orders or followups for this visit yet (${items.length} exist for other dates).`
                : "No orders or followups recorded for this patient yet."}
              <div className="mt-1 text-xs">
                Items appear here after a visit recording is saved, or when staff
                add them in the Compliance tab.
              </div>
              {/* Orders rows are matched to the chart by patient NAME, because
                  `ehr_id` is blank on most audio-derived rows. When the name
                  the recorder stored disagrees with the chart's, the list looks
                  empty and there is no way to tell that from "none exist" —
                  so name the rows we did find. */}
              {candidates.length > 0 && (
                <details className="mt-4 text-left inline-block max-w-full">
                  <summary className="cursor-pointer text-xs text-slate-500 dark:text-slate-400">
                    The orders table has {candidates.length} other patient
                    {candidates.length === 1 ? "" : "s"} — none matched this chart's
                    name ({patientName || "unnamed"}). Show names
                  </summary>
                  <ul className="mt-2 max-h-48 overflow-auto text-xs font-mono text-slate-600 dark:text-slate-300">
                    {candidates.map((c) => (
                      <li key={`${c.name}|${c.ehrId}`} className="py-0.5">
                        {c.name}
                        {c.ehrId ? ` · EHR ${c.ehrId}` : " · no EHR"} · {c.count} item
                        {c.count === 1 ? "" : "s"}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* The tab strip and the date-scope toggle render whenever the load
              succeeded, INCLUDING when the current scope is empty. Hiding them
              behind `visitItems.length > 0` meant a clinician whose visit had
              no rows could never reach the toggle that would have shown them
              the rows that do exist. */}
          {!scanInFlight && !loading && !loadError && (
            <div className="flex items-center gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setTabOverride("note")}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full ring-1 transition-colors ${
                    activeTab === "note"
                      ? "bg-amber-500 text-white ring-amber-500"
                      : "bg-transparent text-slate-600 dark:text-slate-300 ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  }`}
                >
                  📝 From note ({noteItems.length})
                </button>
                <button
                  type="button"
                  onClick={() => setTabOverride("audio")}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full ring-1 transition-colors ${
                    activeTab === "audio"
                      ? "bg-amber-500 text-white ring-amber-500"
                      : "bg-transparent text-slate-600 dark:text-slate-300 ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  }`}
                >
                  🎙 From audio ({audioItems.length})
                </button>
                {/* Paperwork — the orders as PAPER. Every report document in
                    this visit's folder is listed, order-flagged ones badged and
                    first: `is_order` is set on almost nothing until the
                    backfill runs, so filtering on it would ship an empty tab. */}
                <button
                  type="button"
                  onClick={() => setTabOverride("paperwork")}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full ring-1 transition-colors ${
                    activeTab === "paperwork"
                      ? "bg-amber-500 text-white ring-amber-500"
                      : "bg-transparent text-slate-600 dark:text-slate-300 ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  }`}
                >
                  📄 Paperwork
                </button>
                {/* Scope. Shown only when the two scopes actually differ —
                    otherwise it is a control with no effect. */}
                {items.length !== visitScoped.length && (
                  <div className="ml-auto flex items-center text-[11px]">
                    <button
                      type="button"
                      onClick={() => setScopeOverride("visit")}
                      className={`px-2 py-1 rounded-l-md ring-1 ${
                        dateScope === "visit"
                          ? "bg-slate-700 text-white ring-slate-700"
                          : "text-slate-600 dark:text-slate-300 ring-slate-300 dark:ring-slate-600"
                      }`}
                    >
                      This visit ({visitScoped.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setScopeOverride("all")}
                      className={`px-2 py-1 rounded-r-md ring-1 ${
                        dateScope === "all"
                          ? "bg-slate-700 text-white ring-slate-700"
                          : "text-slate-600 dark:text-slate-300 ring-slate-300 dark:ring-slate-600"
                      }`}
                    >
                      All dates ({items.length})
                    </button>
                  </div>
                )}
            </div>
          )}

          {!scanInFlight && !loading && !loadError && activeTab !== "paperwork" && visitItems.length > 0 && (
            <>
              <SelectAllBox
                items={tabItems}
                selectedIds={selectedIds}
                onChange={(on) => setAllOnTab(tabItems, on)}
              />
              <Section
                title="Orders"
                icon="🧾"
                items={orders}
                emptyText="No orders for this patient."
                selectedIds={selectedIds}
                onToggle={toggleSelected}
                schema={schema}
                currentUserId={actorId}
                summaries={summaries}
                onChanged={() => setRefreshKey((k) => k + 1)}
              />
              <Section
                title="Followups"
                icon="📆"
                items={followups}
                emptyText="No followups for this patient."
                selectedIds={selectedIds}
                onToggle={toggleSelected}
                schema={schema}
                currentUserId={actorId}
                summaries={summaries}
                onChanged={() => setRefreshKey((k) => k + 1)}
              />
            </>
          )}

          {/* Generate order — the whole point of the checkboxes. Appears only
              once something is ticked, and carries the selection into the
              Paperwork tab, which fills a blank form with exactly those items. */}
          {!scanInFlight && !loading && activeTab !== "paperwork" && selectedIds.size > 0 && (
            <div className="sticky bottom-0 -mx-5 mt-4 flex items-center gap-3 border-t border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-[#0a0a0f]/95 px-5 py-3 backdrop-blur">
              <span className="text-xs font-semibold">
                {selectedIds.size} selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 underline"
              >
                clear
              </button>
              <button
                type="button"
                onClick={() => setAssigning(true)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg ring-1 ring-sky-400 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-500/10"
              >
                👤 Assign to…
              </button>
              {assigning && (
                <div className="w-[320px]">
                  <ColleaguePicker label="Assign selected to" picked={assignee} onPick={setAssignee} schema={schema} />
                  <div className="flex items-center gap-2 -mt-2">
                    <button
                      type="button"
                      disabled={!assignee || assignBusy}
                      onClick={handleAssignSelected}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-sm disabled:opacity-50"
                    >
                      {assignBusy ? "Assigning…" : `Assign ${selectedIds.size}`}
                    </button>
                    <button
                      type="button"
                      disabled={assignBusy}
                      onClick={() => { setAssigning(false); setAssignee(null); }}
                      className="text-xs px-2 py-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {templates.length > 0 ? (
                <div className="ml-auto flex items-center gap-2">
                  <label htmlFor="order-form-pick" className="text-[11px] font-semibold">
                    Order form
                  </label>
                  <select
                    id="order-form-pick"
                    aria-label="Order form"
                    value={activeTemplate?.name || ""}
                    onChange={(e) => setChosenTemplate(e.target.value)}
                    className="rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1 text-xs max-w-[240px]"
                  >
                    {templates.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.label}
                        {t.name === suggestedTemplate ? "  ★ suggested" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div
                  role="alert"
                  className="ml-auto max-w-[520px] rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/[0.08] px-2.5 py-1.5 text-[11px] text-amber-900 dark:text-amber-200"
                >
                  <span className="font-semibold">⚠️ No order forms uploaded for this clinic.</span>{" "}
                  Nothing to fill in for group{" "}
                  <span className="font-mono font-semibold">{templateGroup || "(none)"}</span>.
                  A Super Admin adds them under Super Admin → Order Form Templates.
                </div>
              )}
              <button
                type="button"
                disabled={!activeTemplate}
                onClick={() => {
                  setGenerateFor(Array.from(selectedIds));
                  setOpenDoc({ path: activeTemplate.path, filename: activeTemplate.name });
                  setTabOverride("paperwork");
                }}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm disabled:opacity-50"
              >
                📄 Generate order
              </button>
            </div>
          )}

          {/* Kept MOUNTED and merely hidden when another tab is in view.
              Unmounting threw away the scan — two AI calls per page — so the
              form vanished on any tab switch and had to be built again.
              h-full so the editor's own h-full still resolves through the
              wrapper, and the `hidden` ATTRIBUTE rather than the Tailwind
              class so no display rule fights it. Only the TAB hides it: a save
              refreshes the orders list, and keying on `loading` too meant
              finishing a form swapped it for that list's spinner. */}
          {openDoc && (
            <div className="h-full min-h-0" hidden={activeTab !== "paperwork"}>
              <OrderPaperworkEditor
                patientId={patientId}
                doc={openDoc}
                orderIds={generateFor}
                paperworkId={openDoc.paperworkId || null}
                allowUnsigned={Boolean(openDoc.paperworkId)}
                signerName={signerName}
                onBack={() => setOpenDoc(null)}
                onSaved={() => {
                  setRefreshKey((k) => k + 1);
                  setSelectedIds(new Set());
                  // generateFor is NOT cleared: it records what the document
                  // still on screen was built from. Nulling it changed the
                  // editor's scan key and re-read the form it had just saved.
                }}
              />
            </div>
          )}

          {activeTab === "paperwork" && !openDoc && (
            <PaperworkTab
              patientId={patientId}
              patientName={patientName}
              schema={schema}
              actorId={actorId}
              refreshKey={refreshKey}
              openPaperworkId={linkedPaperworkId}
              onOpenedPaperwork={linkedPaperworkOpened}
              signerName={signerName}
              onOpenEditor={openFormInEditor}
              onSend={(doc, mode) => setSendTarget({ doc, mode })}
            />
          )}
        </div>

        {/* Rendered INLINE inside the modal, not portalled: Tailwind here is
            scoped to #clinic-root and a portal paints invisible. */}
        {sendTarget && (
          <SendSignedOrderModal
            patientId={patientId}
            patientName={patientName}
            schema={schema}
            doc={sendTarget.doc}
            mode={sendTarget.mode}
            signerName={signerName}
            onClose={() => setSendTarget(null)}
            onSent={() => setRefreshKey((k) => k + 1)}
          />
        )}
      </div>
    </div>
  );
}
